import {AxiosResponse} from 'axios';
import {useQuery} from '@tanstack/react-query';
import axiosInstance, {apiGet} from '../axiosConfig';
import {IApiDataResponse, IApiPaginationResponse} from '../dto/API.dto';
import {
  ICompany,
  ICompanyDetails,
  IContentTransferCompany,
} from '../dto/Company.dto';
import {IDataGridSearchSort} from '../dto/Html.dto';

export const apiGetCompanies = (params: ICompanyQueryParams) => {
  return apiGet(`/v1/company`, {params: params}).then(
    (response: AxiosResponse<IApiDataResponse<ICompany[]>>) =>
      response.data.data,
  );
};

export const apiGetCompaniesLimitData = (params: ICompanyQueryParams) => {
  return apiGet(`/v1/company/limitCompanyData`, {params: params}).then(
    (response: AxiosResponse<IApiDataResponse<IContentTransferCompany[]>>) =>
      response.data,
  );
};

export const apiGetCompaniesLimitDataPaginate = (
  query: IDataGridSearchSort,
) => {
  return apiGet(`/v1/company/limitCompanyDataPaginate`, {params: query}).then(
    (response: AxiosResponse<IApiPaginationResponse<ICompany[]>>) =>
      response.data,
  );
};

export const getCompanyDetailsApiPath = (companyId: number) => {
  return `/v1/company/${companyId}`;
};

export const apiGetCompany = (companyId: number) => {
  return apiGet(getCompanyDetailsApiPath(companyId)).then(
    (res: AxiosResponse<IApiDataResponse<ICompanyDetails>>) => res.data.data,
  );
};

export const useRqGetCompany = (companyId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: [`/v1/company/${companyId}`],
    queryFn: () => apiGetCompany(companyId),
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

export const useRqGetCompanies = (
  params: ICompanyQueryParams,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['api/v1/company', params],
    queryFn: () => apiGetCompanies(params),
    staleTime: 600000,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

export const useRqGetCompaniesLimitData = (
  params: ICompanyQueryParams,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['api/v1/company/limitCompanyData', params],
    queryFn: () => apiGetCompaniesLimitData(params),
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};
export const useRqGetCompaniesLimitDataPaginate = (
  query: IDataGridSearchSort,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['api/v1/company/limitCompanyDataPaginate', query],
    queryFn: () => apiGetCompaniesLimitDataPaginate(query),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: enabled,
  });
};
export type ITrashedQueryType = 'with' | 'with_out' | 'only';

export type ICompanyQueryParams = {
  search?: string;
  product_respresentive?: number[];
  psm?: number[];
  product_id?: number[];
  cmc_product?: string[];
  product_type?: string[];
  secondary_product_type?: string[];
  level_product_type?: string[];
  state?: number[];
  sort_field?: string;
  sort_order?: string;
  trashed_data?: ITrashedQueryType;
  module?: 'tnn-campaign' | 'music-section' | 'other' | 'admin-partner-list';
  suspended?: boolean;
  active?: boolean;
  archived?: boolean;
  not_okay_to_download?: boolean;
  type?: string; // Used for global content exception partner list to differentiate from normal company list query
  partner_id?: number;
  include_archived?: boolean; // Content Transfer Portal "From" side only - opts archived partners in
};

export const apiPaginateCompanies = (params: ICompaniesQueryParmas) => {
  return apiGet(`/v1/companies`, {params: params}).then(
    (response: AxiosResponse<IApiPaginationResponse<ICompanyDetails[]>>) =>
      response.data,
  );
};

export const useRqPaginateCompanies = (
  params: ICompaniesQueryParmas,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['api/v1/companies', params],
    queryFn: () => apiPaginateCompanies(params),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
  });
};

export const getAllCompanies = (params: ICompaniesQueryParmas) => {
  return apiGet(`/v1/companies/allCompaniesList`, {params: params}).then(
    (response: AxiosResponse<IApiPaginationResponse<ICompanyDetails[]>>) =>
      response.data,
  );
};

export const useRqAllCompanies = (
  params: ICompaniesQueryParmas,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['api/v1/companies/allCompaniesList', params],
    queryFn: () => getAllCompanies(params),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
  });
};

export const apiPaginateCompaniesGlobal = (params: ICompaniesQueryParmas) => {
  return apiGet(`/v1/companies/global`, {params: params}).then(
    (response: AxiosResponse<IApiPaginationResponse<ICompany[]>>) =>
      response.data,
  );
};

export const useRqPaginateCompaniesGlobal = (
  params: ICompaniesQueryParmas,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['api/v1/companies/global', params],
    queryFn: () => apiPaginateCompaniesGlobal(params),
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000, // 1 hr
    retry: false,
    enabled: enabled,
  });
};

export type ICompaniesQueryParmas = ICompanyQueryParams & {
  page: number;
  per_page: number;
};

// Bill details
export type PricingDetail = {
  name: string;
  count: number;
  price: number;
  isDiscountByDollor?: boolean;
  discount_label?: string | null;
  discount_other_text?: string | null;
};
export type DistrictBreakdown = {
  high_school: PricingDetail;
  middle_school: PricingDetail;
  district_additional_station: PricingDetail;
  high_school_additional_station: PricingDetail;
  middle_school_additional_station: PricingDetail;
};
export type BillDetailsProp = {
  product: {
    cmc_product: string;
    primary: string;
  };
  level: PricingDetail;
  level_charge: PricingDetail;
  deleted_full_station: PricingDetail;
  deleted_mirror_station: PricingDetail;
  deleted_odo_station: PricingDetail;
  updated_full_station: PricingDetail;
  updated_mirror_station: PricingDetail;
  updated_odo_station: PricingDetail;
  full_station: PricingDetail;
  odo_station: PricingDetail;
  mirror_station: PricingDetail;
  setup: PricingDetail;
  discount: PricingDetail;
  service_total: PricingDetail;
  laptop: PricingDetail;
  total_invoice: PricingDetail;
  previous_total: PricingDetail;
  grand_total: PricingDetail;
  district_breakdown?: DistrictBreakdown;
  note?: string;
};
export const getCompanyBillApiPath = (companyId: number) =>
  `/v1/company/${companyId}/add-on`;
const getCompanyBill = (companyId: number) => {
  return axiosInstance
    .get(getCompanyBillApiPath(companyId))
    .then((response: AxiosResponse<IApiDataResponse<BillDetailsProp>>) => {
      return response.data.data;
    });
};

export const useRqCompanyBill = (
  companyId: number,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [getCompanyBillApiPath(companyId)],
    queryFn: () => getCompanyBill(companyId),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
  });
};

// Station name validation
export type StationNameExistsResponse = {
  exists: boolean;
  message?: string;
};

export const apiCheckStationNameExists = (
  companyId: number,
  stationName: string,
) => {
  return apiGet(`/v1/company/${companyId}/station-name-exists`, {
    params: {
      station_name: stationName,
    },
  }).then(
    (response: AxiosResponse<IApiDataResponse<StationNameExistsResponse>>) =>
      response.data.data,
  );
};

export const useRqCheckStationNameExists = (
  companyId: number,
  stationName: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [`/v1/company/${companyId}/station-name-exists`, stationName],
    queryFn: () => apiCheckStationNameExists(companyId, stationName),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled && stationName.length > 0,
  });
};
